import { token } from '../../theme';
import { CanvasComponent } from '../canvas-base';

/**
 * Oscilloscope-style waveform. Phosphor-green trace on a graticule grid,
 * with corner annotations (CH1, V/div, t/div, TRIG).
 */
export class Scope extends CanvasComponent {
  private analyser: AnalyserNode | null = null;
  private data: Uint8Array<ArrayBuffer> | null = null;

  private readonly divsX = 10;
  private readonly divsY = 8;

  private colTrace = '#a3ff00';
  private colGridMinor = 'rgba(80,110,160,0.08)';
  private colGridMajor = 'rgba(80,140,200,0.18)';
  private colGridAxis = 'rgba(120,180,240,0.34)';
  private colSurface = '#0a0e16';
  private colBorder = '#1a2332';
  private colDim = '#7e8a9a';
  private colMute = '#3e4858';

  constructor() {
    super();
    this.el.classList.add('scope');
    this.attachLifecycle();
    this.cacheColors();
  }

  bind(analyser: AnalyserNode | null): void {
    this.analyser = analyser;
    if (analyser) this.data = new Uint8Array(analyser.fftSize);
  }

  protected override onThemeChanged(): void {
    this.cacheColors();
  }

  private cacheColors(): void {
    this.colTrace = token('trace-wave');
    this.colGridMinor = token('graticule-minor');
    this.colGridMajor = token('graticule-major');
    this.colGridAxis = token('graticule-axis');
    this.colSurface = token('display-bg');
    this.colBorder = token('border-hairline');
    this.colDim = token('text-dim');
    this.colMute = token('text-mute');
  }

  protected draw(): void {
    if (this.w === 0 || this.h === 0) return;
    const { ctx } = this;

    // Background
    ctx.fillStyle = this.colSurface;
    ctx.fillRect(0, 0, this.w, this.h);

    this.drawGraticule();
    this.drawAnnotations();
    this.drawTrace();
    this.drawBorder();
  }

  private drawGraticule(): void {
    const { ctx, w, h, divsX, divsY } = this;
    const stepX = w / divsX;
    const stepY = h / divsY;

    // Minor — dotted, every 1/5 of a major
    ctx.lineWidth = 1;
    ctx.strokeStyle = this.colGridMinor;
    ctx.setLineDash([1, 3]);
    ctx.beginPath();
    for (let x = 0; x <= divsX * 5; x++) {
      const px = (x * stepX) / 5;
      ctx.moveTo(px, 0);
      ctx.lineTo(px, h);
    }
    for (let y = 0; y <= divsY * 5; y++) {
      const py = (y * stepY) / 5;
      ctx.moveTo(0, py);
      ctx.lineTo(w, py);
    }
    ctx.stroke();
    ctx.setLineDash([]);

    // Major
    ctx.strokeStyle = this.colGridMajor;
    ctx.beginPath();
    for (let x = 0; x <= divsX; x++) {
      ctx.moveTo(Math.round(x * stepX) + 0.5, 0);
      ctx.lineTo(Math.round(x * stepX) + 0.5, h);
    }
    for (let y = 0; y <= divsY; y++) {
      ctx.moveTo(0, Math.round(y * stepY) + 0.5);
      ctx.lineTo(w, Math.round(y * stepY) + 0.5);
    }
    ctx.stroke();

    // Axis (centerlines)
    ctx.strokeStyle = this.colGridAxis;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(h / 2) + 0.5);
    ctx.lineTo(w, Math.round(h / 2) + 0.5);
    ctx.moveTo(Math.round(w / 2) + 0.5, 0);
    ctx.lineTo(Math.round(w / 2) + 0.5, h);
    ctx.stroke();
  }

  private drawAnnotations(): void {
    const { ctx } = this;
    ctx.font = `${10}px ${token('font-mono')}`;
    ctx.textBaseline = 'top';

    ctx.fillStyle = this.colTrace;
    ctx.fillText('CH1  WAVE', 8, 6);

    ctx.fillStyle = this.colDim;
    ctx.textAlign = 'right';
    ctx.fillText('1.0  V/div', this.w - 8, 6);

    ctx.fillStyle = this.colMute;
    ctx.textAlign = 'left';
    ctx.fillText('t  AUTO', 8, this.h - 14);

    ctx.textAlign = 'right';
    ctx.fillText('TRIG  EDGE', this.w - 8, this.h - 14);
    ctx.textAlign = 'left';
  }

  private drawTrace(): void {
    if (!this.analyser || !this.data) return;
    this.analyser.getByteTimeDomainData(this.data);
    const { ctx, w, h } = this;
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = this.colTrace;
    ctx.shadowColor = this.colTrace;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    const slice = w / this.data.length;
    let x = 0;
    for (let i = 0; i < this.data.length; i++) {
      const v = this.data[i] / 128.0;
      const y = (v * h) / 2;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      x += slice;
    }
    ctx.stroke();
    ctx.shadowBlur = 0;
  }

  private drawBorder(): void {
    const { ctx, w, h } = this;
    ctx.strokeStyle = this.colBorder;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  }
}
